import { useEffect, useState } from 'react';

/**
 * Viewport breakpoints of the dashboard sidebar, aligned with Tailwind's defaults: the full
 * sidebar from lg (1024px), an icon-only rail on md..lg (768..1023px) and the off-canvas
 * drawer below md (768px) opened from the top-bar burger.
 */
export const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';
export const TABLET_MEDIA_QUERY = '(min-width: 768px)';

/** Which sidebar presentation the current viewport gets. */
export type SidebarMode = 'full' | 'icons' | 'drawer';

/**
 * One matchMedia subscription, following the room's useIsMobile idiom: the initial value is
 * read synchronously, changes stream through the listener. jsdom answers false by default;
 * tests mock window.matchMedia to force a path.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState((): boolean => window.matchMedia(query).matches);

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent): void => {
      setMatches(event.matches);
    };
    setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    return () => {
      mql.removeEventListener('change', onChange);
    };
  }, [query]);

  return matches;
}

/** Derives the sidebar presentation from the two breakpoints; pure, so suites classify directly. */
export function sidebarMode(desktop: boolean, tablet: boolean): SidebarMode {
  if (desktop) {
    return 'full';
  }
  return tablet ? 'icons' : 'drawer';
}

/** Subscribes to both breakpoints and reports the resulting sidebar presentation. */
export function useSidebarMode(): SidebarMode {
  const desktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const tablet = useMediaQuery(TABLET_MEDIA_QUERY);
  return sidebarMode(desktop, tablet);
}
